<template>
  <div>
    <Head title="Transferencia de Productos" />
    
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold">
        Transferencia de Productos - {{ deposit.name }}
      </h1>
    </div>

    <!-- Búsqueda de Productos -->
    <div class="mb-6">
      <div class="flex flex-wrap gap-4">
        <text-input 
          v-model="form.search.terms"
          label="Buscar por nombre o código"
          class="w-full md:w-1/3"
          placeholder="Ingrese término de búsqueda..."
        />
      </div>
    </div>

    <!-- Lista de Productos Disponibles -->
    <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
      <div class="p-4 bg-gray-50 border-b">
        <h2 class="text-lg font-semibold text-gray-800">Productos Disponibles</h2>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Código</th>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Producto</th>
              <th class="px-4 py-2 text-center text-sm font-medium text-gray-600">Stock Disponible</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="product in products.data" 
              :key="product.id"
              class="hover:bg-gray-50 cursor-pointer"
              @click="getProductStock(product.depositInventory, deposit.id) > 0 ? selectProduct(product) : alert('No hay stock disponible')"
            >
              <td class="px-4 py-2 text-sm text-gray-900">{{ product.code }}</td>
              <td class="px-4 py-2 text-sm text-gray-900">{{ product.name }}</td>
              <td class="px-4 py-2 text-sm text-center text-gray-900">
                {{ getProductStock(product.depositInventory, deposit.id) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Productos Seleccionados -->
    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="p-4 bg-gray-50 border-b">
        <h2 class="text-lg font-semibold text-gray-800">Productos a Transferir</h2>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Producto</th>
              <th class="px-4 py-2 text-left text-sm font-medium text-gray-600">Depósito Destino</th>
              <th class="px-4 py-2 text-center text-sm font-medium text-gray-600">Cantidad</th>
              <th class="px-4 py-2 text-center text-sm font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in form.products" :key="product.id">
              <td class="px-4 py-2 text-sm text-gray-900">
                {{ product.name }}
              </td>
              <td class="px-4 py-2">
                <select-input 
                  v-model="form.products[index].deposit"
                  option-label="name"
                  option-value="id"
                  class="w-full"
                  placeholder="Seleccione un depósito"
                >
                  <option v-for="dep in filteredDeposits" :key="dep.id" :value="dep.id">
                    {{ dep.name }}
                  </option>
                </select-input>
              </td>
              <td class="px-4 py-2">
                <text-input 
                  v-model="form.products[index].quantity"
                  type="number"
                  :max="getProductStock(product.depositInventory, deposit.id)"
                  min="1"
                  class="w-full"
                />
              </td>
              <td class="px-4 py-2 text-center whitespace-nowrap">
                <button 
                  v-if="product.has_imei || (product.available_imeis && product.available_imeis.length > 0)"
                  type="button"
                  class="text-indigo-600 hover:text-indigo-900 mr-3"
                  @click="openImeiModal(index)"
                  title="Seleccionar IMEIs"
                >
                  <i class="fa fa-barcode text-lg"></i>
                </button>
                <button 
                  type="button"
                  class="text-red-600 hover:text-red-800"
                  @click="removeProduct(product.id)"
                  title="Eliminar producto"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="p-4 bg-gray-50 border-t">
        <button 
          class="btn-indigo"
          type="button"
          @click="submitTransfer"
        >
          Generar Transferencia
        </button>
      </div>
    </div>

    <!-- Modal Selección de IMEIs -->
    <div v-if="imeiModal.show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div class="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <h3 class="text-lg font-bold text-gray-800">
            <i class="fa fa-barcode mr-2 text-indigo-600"></i>
            Seleccionar IMEIs
          </h3>
          <button @click="closeImeiModal" class="text-gray-400 hover:text-gray-600">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="p-6">
          <div class="mb-4">
            <p class="text-sm font-semibold text-gray-800">
              Producto: <span class="font-normal">{{ form.products[imeiModal.productIndex]?.name }}</span>
            </p>
            <p class="text-sm font-semibold text-gray-800">
              Cantidad a transferir: 
              <span class="text-indigo-600 font-bold">
                {{ form.products[imeiModal.productIndex]?.quantity || 0 }}
              </span>
            </p>
            <p class="text-xs text-gray-500 mt-1">
              Seleccionados: {{ form.products[imeiModal.productIndex]?.selected_imeis?.length || 0 }} de {{ form.products[imeiModal.productIndex]?.quantity || 0 }}
            </p>
          </div>

          <div v-if="!(form.products[imeiModal.productIndex]?.quantity > 0)" class="p-3 bg-yellow-50 text-yellow-800 rounded text-sm mb-4">
            Primero indique una cantidad mayor a 0 en la tabla para poder seleccionar IMEIs.
          </div>

          <div v-else>
            <!-- Buscador dentro del modal -->
            <input 
              v-model="imeiModal.searchQuery" 
              type="text" 
              placeholder="Buscar o escanear IMEI..." 
              class="w-full px-3 py-2 border rounded mb-3 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @keydown.enter.prevent="handleModalSearchEnter"
            />

            <!-- Lista de IMEIs Disponibles -->
            <div class="max-h-60 overflow-y-auto border rounded divide-y">
              <div 
                v-for="imeiItem in modalAvailableImeis" 
                :key="imeiItem.id"
                class="flex items-center justify-between p-2.5 hover:bg-gray-50 cursor-pointer text-sm"
                @click="toggleImeiSelection(imeiItem.id)"
              >
                <div class="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    :checked="isImeiSelected(imeiItem.id)"
                    class="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4 pointer-events-none"
                  />
                  <span class="font-mono text-gray-800">{{ imeiItem.imei }}</span>
                </div>
                <span v-if="isImeiSelected(imeiItem.id)" class="text-xs text-indigo-600 font-medium">
                  Seleccionado
                </span>
              </div>

              <div v-if="!modalAvailableImeis.length" class="p-4 text-center text-sm text-gray-500">
                No hay IMEIs disponibles en este depósito para este producto.
              </div>
            </div>
          </div>
        </div>

        <div class="px-6 py-3 bg-gray-50 border-t flex justify-end">
          <button 
            type="button"
            class="btn-indigo"
            @click="closeImeiModal"
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import SelectInput from '@/Shared/SelectInput.vue'
import TextInput from '@/Shared/TextInput'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

export default {
  name: 'TransferCreate',

  components: {
    Head,
    Link,
    SelectInput,
    TextInput,
  },

  layout: Layout,

  props: {
    depositTransfers: Object,
    depositId: Number,
    products: Object,
    brands: Object,
    parts: Object,
    models: Object,
    deposits: Object,
    regions: Object,
    deposit: Object,
  },

  data() {
    return {
      form: this.$inertia.form({
        search: {
          terms: '',
          deposit: this.depositId,
          brand_id: '',
          part_id: '',
          truck_model_id: '',
        },
        products: [],
      }),
      imeiModal: {
        show: false,
        productIndex: null,
        searchQuery: '',
      },
    }
  },

  computed: {
    canSubmit() {
      return this.form.products.length > 0 && 
        this.form.products.every(product => 
          product.quantity > 0 && 
          product.deposit && 
          product.quantity <= this.getProductStock(product.depositInventory, this.deposit.id) &&
          (!product.has_imei || (product.selected_imeis && product.selected_imeis.length === Number(product.quantity)))
        )
    },

    filteredDeposits() {
      return this.deposits.filter(deposit => deposit.id !== this.deposit.id)
    },

    modalAvailableImeis() {
      if (this.imeiModal.productIndex === null || !this.form.products[this.imeiModal.productIndex]) {
        return []
      }
      const product = this.form.products[this.imeiModal.productIndex]
      const imeis = product.available_imeis || []
      if (!this.imeiModal.searchQuery) {
        return imeis
      }
      const query = this.imeiModal.searchQuery.toLowerCase().trim()
      return imeis.filter(i => i.imei && i.imei.toLowerCase().includes(query))
    },
  },

  watch: {
    'form.search': {
      handler: throttle(function() {
        this.onSearchProductsChange()
      }, 300),
      deep: true
    }
  },

  methods: {
    alert(message) {
      this.$swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: message,
      })
    },
    getProductStock(inventory, depositId) {
      const stock = inventory.find(inv => inv.deposit_id === depositId)
      return stock ? stock.quantity : 0
    },

    onSearchProductsChange() {
      this.$inertia.get(
        `/deposits/${this.depositId}/transfer/create`,
        pickBy(this.form.search),
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        }
      )
    },

    selectProduct(product) {
      const exists = this.form.products.some(p => p.id === product.id)
      if (!exists) {
        this.form.products.push({
          ...product,
          quantity: 0,
          deposit: null,
          selected_imeis: [],
        })
      }
    },

    removeProduct(productId) {
      if (confirm('¿Está seguro que desea eliminar este producto de la transferencia?')) {
        this.form.products = this.form.products.filter(p => p.id !== productId)
      }
    },

    openImeiModal(index) {
      this.imeiModal.productIndex = index
      this.imeiModal.searchQuery = ''
      this.imeiModal.show = true
    },

    closeImeiModal() {
      this.imeiModal.show = false
      this.imeiModal.productIndex = null
      this.imeiModal.searchQuery = ''
    },

    toggleImeiSelection(imeiId) {
      const product = this.form.products[this.imeiModal.productIndex]
      if (!product) return
      if (!product.selected_imeis) {
        product.selected_imeis = []
      }
      const idx = product.selected_imeis.indexOf(imeiId)
      if (idx > -1) {
        product.selected_imeis.splice(idx, 1)
      } else {
        if (product.selected_imeis.length >= Number(product.quantity)) {
          this.alert(`Ya ha seleccionado la cantidad requerida de IMEIs (${product.quantity})`)
          return
        }
        product.selected_imeis.push(imeiId)
      }
    },

    isImeiSelected(imeiId) {
      const product = this.form.products[this.imeiModal.productIndex]
      return product && product.selected_imeis ? product.selected_imeis.includes(imeiId) : false
    },

    handleModalSearchEnter() {
      if (this.modalAvailableImeis.length === 1) {
        const item = this.modalAvailableImeis[0];
        if (!this.isImeiSelected(item.id)) {
          this.toggleImeiSelection(item.id);
        }
        this.imeiModal.searchQuery = '';
      }
    },

    submitTransfer() {
      if (this.form.products.length === 0) {
        this.alert('Por favor seleccione al menos un producto a transferir.')
        return
      }

      for (const product of this.form.products) {
        if (!product.deposit) {
          this.alert(`Por favor seleccione el "Depósito Destino" para el producto "${product.name}".`)
          return
        }

        if (!(product.quantity > 0)) {
          this.alert(`La cantidad para el producto "${product.name}" debe ser mayor a 0.`)
          return
        }

        const maxStock = this.getProductStock(product.depositInventory, this.deposit.id)
        if (product.quantity > maxStock) {
          this.alert(`La cantidad indicada (${product.quantity}) supera el stock disponible (${maxStock}) para "${product.name}".`)
          return
        }

        if (product.has_imei) {
          const count = product.selected_imeis ? product.selected_imeis.length : 0
          if (count !== Number(product.quantity)) {
            this.alert(`Debe seleccionar exactamente ${product.quantity} IMEI(s) para el producto "${product.name}". (Actualmente seleccionados: ${count})`)
            return
          }
        }
      }

      this.form.post(`/deposits/${this.depositId}/transfer`)
    }
  }
}
</script>
